/**
 * ModerationLabelsContent Component
 *
 * Displays detected labels with confidence scores as badges.
 * Each label shows the name and confidence percentage.
 *
 * @example
 * ```tsx
 * <ModerationLabelsContent
 *   labels={[
 *     { id: 'lbl1', name: 'Profanity', confidence: 95 },
 *     { id: 'lbl2', name: 'Harassment', confidence: 82 }
 *   ]}
 * />
 * ```
 */

import { Badge, BadgeVariant, Text, Icon, IconSize, Label } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';

import * as styles from './ModerationCaseCard.css';

/**
 * Label with confidence score interface
 */
export interface LabelWithConfidence {
  /**
   * Unique identifier for the label
   */
  id: string;

  /**
   * Label name
   */
  name: string;

  /**
   * Confidence score (0-100)
   */
  confidence: number;

  /**
   * Optional severity level
   */
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Props for ModerationLabelsContent
 */
export interface ModerationLabelsContentProps {
  /**
   * Array of labels with confidence scores
   */
  labels: LabelWithConfidence[];

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * Get badge variant based on confidence level
 */
function getBadgeVariant(confidence: number): BadgeVariant {
  if (confidence >= 90) {
    return BadgeVariant.Error;
  } else if (confidence >= 70) {
    return BadgeVariant.Warning;
  }

  return BadgeVariant.Default;
}

/**
 * ModerationLabelsContent Component
 *
 * Renders detected labels as badges with confidence percentages.
 * Badge colors indicate confidence level (red for high, yellow for medium, gray for low).
 */
export function ModerationLabelsContent({ labels, className }: ModerationLabelsContentProps): ReactNode {
  if (!labels || labels.length === 0) {
    return (
      <Text color="secondary" size="sm">
        No labels detected
      </Text>
    );
  }

  const combinedClassName = [styles.labelsContainer, className].filter(Boolean).join(' ');

  return (
    <div className={combinedClassName}>
      <div className={styles.labelsWrapper}>
        {/* Inline small header */}
        <div className={styles.labelsHeaderGroup}>
          <Icon icon={Label} iconSize={IconSize.XSmall} />
          <span className={styles.labelsHeaderText}>Labels</span>
        </div>

        {/* Badges inline */}
        {labels.map(label => (
          <Badge key={label.id} className={styles.labelBadge} variant={getBadgeVariant(label.confidence)}>
            <span className={styles.labelName}>{label.name}</span>
            <span className={styles.labelConfidence}> ({label.confidence}%)</span>
          </Badge>
        ))}
      </div>
    </div>
  );
}

ModerationLabelsContent.displayName = 'ModerationLabelsContent';
